import React, { useState, useId } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Modal, ModalTitle, ModalBody, ModalFooter } from 'design-system/src/components/Modal';
import Button from 'design-system/src/components/Button/Button';
import { TabList } from 'design-system/src/components/TabList/TabList';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { RadioBlockGroup, RadioOptionWrapper } from 'design-system/src/components/Radio/Radio';
import { Input } from '../Input';
import { useGeocode } from '../AddressAutocomplete/useGeocode';
import { formatTin, requiredFieldRule } from '../../utils';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { client } from '../../api';
import { type OfficeGroup } from './types';
import { officeGroupKey, splitAddress } from './utils';
import * as Sentry from '@sentry/react';

type Tab = 'new' | 'existing';

type FormData = {
  name: string;
  tin: string;
};

type NewOfficeFormProps = {
  control: ReturnType<typeof useForm<FormData>>['control'];
  errors: ReturnType<typeof useForm<FormData>>['formState']['errors'];
};

const NewOfficeForm: React.FC<NewOfficeFormProps> = ({ control, errors }) => (
  <>
    <Controller
      control={control}
      name="name"
      rules={{ required: requiredFieldRule('Office name') }}
      render={({ field }) => (
        <Input label="Office name" placeholder="e.g. Downtown office" hasError={!!errors.name} {...field} />
      )}
    />
    {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}

    <Controller
      control={control}
      name="tin"
      render={({ field }) => (
        <Input
          label="TIN"
          placeholder="987-65-4321"
          className="w-full"
          {...field}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(formatTin(e.target.value))}
        />
      )}
    />
  </>
);

type ExistingOfficeListProps = {
  officeGroups: OfficeGroup[];
  selectedGroupKey: string | null;
  onSelect: (key: string) => void;
};

const ExistingOfficeList: React.FC<ExistingOfficeListProps> = ({ officeGroups, selectedGroupKey, onSelect }) => (
  <div className="flex flex-col gap-1">
    <Typography styledAs="bodySmallSpaceGrotesk" weight={500}>
      Your offices
    </Typography>
    <div className="max-h-[220px] overflow-y-auto">
      <RadioBlockGroup $variant="outlined">
        {officeGroups.map((group) => {
          const key = officeGroupKey(group);
          const { street } = splitAddress(group.displayOffice.address);
          const extraCount = group.allOffices.length - 1;

          return (
            <RadioOptionWrapper
              key={key}
              id={`office-${key}`}
              name="existingOffice"
              value={key}
              checked={selectedGroupKey === key}
              onChange={() => onSelect(key)}
              variant="outlined"
            >
              <div className="flex flex-col gap-0.5">
                <Typography styledAs="bodySmallDMSans" weight={600}>
                  {group.name}
                </Typography>
                <Typography styledAs="bodyExtraSmallDMSans" color="var(--grayscale-600)">
                  {extraCount > 0 ? `${street} (+${extraCount} more)` : group.displayOffice.address}
                </Typography>
              </div>
            </RadioOptionWrapper>
          );
        })}
      </RadioBlockGroup>
    </div>
  </div>
);

type AddUnmatchedAddressAsOfficeModalProps = {
  /** Called when the modal should close. */
  onClose: () => void;
  /** The unmatched address to assign to a new or existing office. */
  address: string;
  /** ID of the existing auto-created office for this address, if one exists. */
  officeId: string | null;
  /** Grouped offices available for the "Existing office" tab. */
  officeGroups: OfficeGroup[];
};

const AddUnmatchedAddressAsOfficeModal: React.FC<AddUnmatchedAddressAsOfficeModalProps> = ({
  onClose,
  address,
  officeId,
  officeGroups,
}) => {
  const queryClient = useQueryClient();
  const { geocode } = useGeocode();
  const tabListId = useId();
  const tabPanelId = useId();
  const [geocodeError, setGeocodeError] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState<Tab>('new');
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: { name: '', tin: '' },
  });

  const onMutationSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['offices-v2'] });
    queryClient.invalidateQueries({ queryKey: ['unmatched-addresses'] });
    toast.custom(() => <Toast variant="success">Office added</Toast>);
    onClose();
  };

  const { mutate: createOffice, isPending: isCreatePending } = useMutation({
    mutationFn: (data: { name: string; tin: string | null; address: string; latitude: number; longitude: number }) =>
      client.post('/settings/offices', data),
    onSuccess: onMutationSuccess,
    onError: (error: any) => {
      if (error?.response?.status === 409) {
        toast.custom(() => <Toast variant="error">An office with this address already exists</Toast>);
      } else {
        Sentry.captureException(error);
        toast.custom(() => <StandardErrorToast />);
      }
    },
  });

  const { mutate: updateOffice, isPending: isUpdatePending } = useMutation({
    mutationFn: (data: { name: string; tin: string | null }) => client.patch(`/settings/offices/${officeId}`, data),
    onSuccess: onMutationSuccess,
    onError: (error) => {
      Sentry.captureException(error);
      toast.custom(() => <StandardErrorToast />);
    },
  });

  const isPending = isCreatePending || isUpdatePending;

  const submitNameAndTin = async (name: string, tin: string | null) => {
    if (officeId) {
      updateOffice({ name, tin });
    } else {
      setGeocodeError(null);
      const parsed = await geocode(address);
      if (!parsed || parsed.latitude == null || parsed.longitude == null) {
        setGeocodeError('Could not verify address location. Please try again.');
        return;
      }
      createOffice({
        name,
        tin,
        address: parsed.formatted_address || address,
        latitude: parsed.latitude,
        longitude: parsed.longitude,
      });
    }
  };

  const onSubmitNew = async (data: FormData) => {
    await submitNameAndTin(data.name, data.tin || null);
  };

  const onSubmitExisting = async () => {
    const selected = officeGroups.find((g) => officeGroupKey(g) === selectedGroupKey);
    if (!selected) return;
    await submitNameAndTin(selected.name, selected.tin);
  };

  const watchedName = watch('name');
  const shouldShowTabs = officeGroups.length > 0;
  const effectiveTab: Tab = shouldShowTabs ? currentTab : 'new';
  const isSaveDisabled = effectiveTab === 'new' ? !watchedName?.trim() : !selectedGroupKey;
  const handleSave = effectiveTab === 'new' ? handleSubmit(onSubmitNew) : onSubmitExisting;
  const handleTabSelect = (value: string) => {
    if (value === 'existing' && officeGroups.length === 0) {
      toast.custom(() => <StandardErrorToast />);
      return;
    }
    setCurrentTab(value as Tab);
  };

  return (
    <Modal onClose={onClose}>
      <ModalTitle>Assign address to new or existing office</ModalTitle>
      <ModalBody>
        <div className="flex flex-col gap-4">
          {shouldShowTabs && (
            <TabList
              tabs={[
                { value: 'new', label: 'New office' },
                { value: 'existing', label: 'Existing office' },
              ]}
              selectedValue={currentTab}
              onSelect={handleTabSelect}
              tabListId={tabListId}
              tabPanelId={tabPanelId}
              ariaLabel="Select office assignment mode"
            />
          )}

          <div
            role={shouldShowTabs ? 'tabpanel' : undefined}
            id={shouldShowTabs ? tabPanelId : undefined}
            aria-labelledby={shouldShowTabs ? tabListId : undefined}
          >
            {effectiveTab === 'new' ? (
              <NewOfficeForm control={control} errors={errors} />
            ) : (
              <ExistingOfficeList
                officeGroups={officeGroups}
                selectedGroupKey={selectedGroupKey}
                onSelect={setSelectedGroupKey}
              />
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Typography styledAs="bodySmallSpaceGrotesk" weight={500}>
              Selected address:
            </Typography>
            <div className="rounded-md bg-grayscale-50 p-3">
              <Typography styledAs="bodyDMSans">{address}</Typography>
            </div>
          </div>

          {geocodeError && <ErrorMessage>{geocodeError}</ErrorMessage>}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" disabled={isPending} onClick={onClose}>
          Back
        </Button>
        <Button variant="primary" disabled={isSaveDisabled} isLoading={isPending} onClick={handleSave}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddUnmatchedAddressAsOfficeModal;
